import { ReceiptText } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/button'
import { ContractEditIcon } from '@/components/icons/contract-edit'
import { LoaderCircle } from '@/components/loader-circle'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/select'
import { useAuth } from '@/contexts'
import { ProfileType } from '@/enum/profile-type'
import { formatNumberToCurrency } from '@/helper/currency'
import {
  CardDataProps,
  ContractFinancesData,
  SponsorshipList,
} from '@/models/finances-tab'
import api from '@/services/api'
import { FormatLocaleDate } from '@/utils/format-locale-date'

import { MonthlyStatement } from './monthly-statement'

const CardData = ({ title, value }: CardDataProps) => {
  return (
    <div className="border-bios-primary flex flex-col gap-2 border-l-[3px] pl-2">
      <h4 className="text-monochromatic-500 text-lg font-bold 2xl:text-xl">
        {title}
      </h4>
      <p className="text-monochromatic-400 text-lg 2xl:text-xl">{value}</p>
    </div>
  )
}

interface ReceiptResponse {
  url_recibo_patrocinador: string
  nome_recibo?: string
}

interface FinancesTabProps {
  isSponsoring?: boolean
}

export function FinancesTab({ isSponsoring = false }: FinancesTabProps) {
  const { idProperty } = useParams()
  const { pathname } = useLocation()
  const { currentProfile } = useAuth()
  const [contractId, setContractId] = useState<string>()
  const [sponsorshipList, setSponsorshipList] = useState<
    SponsorshipList[] | null
  >([])
  const [contractFinancesData, setContractFinancesData] =
    useState<ContractFinancesData>()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const isSponsor =
    currentProfile === ProfileType.Sponsor && pathname.startsWith('/property')

  useEffect(() => {
    const abortController = new AbortController()
    const signal = abortController.signal

    async function getData() {
      setIsLoading(true)

      await api
        .get<SponsorshipList[] | null>(`/sponsorship/list/${idProperty}`, {
          signal,
        })
        .then(({ data }) => {
          setSponsorshipList(data)

          if (data !== null) {
            setContractId(data[0].id_contrato)
          }
        })
        .catch((error) => {
          toast.error(error.response.data.message, {
            className: 'finances-tab-error-message',
          })
        })
        .finally(() => setIsLoading(false))
    }

    getData()

    return () => {
      abortController.abort()
    }
  }, [idProperty])

  useEffect(() => {
    const abortController = new AbortController()

    const signal = abortController.signal

    async function getContractData() {
      setIsLoading(true)

      let path = `/sponsorship/financial/data`

      if (isSponsor) {
        path = `/sponsorship/financial/data/to/sponsors`
      }

      await api
        .get<ContractFinancesData>(path.concat(`/${contractId}`), { signal })
        .then(({ data }) => {
          setContractFinancesData(data)
        })
        .catch((error) => {
          toast.error(error.response.data.message, {
            className: 'finances-tab-error-message',
          })
        })
        .finally(() => setIsLoading(false))
    }

    if (contractId) {
      getContractData()
    }

    return () => {
      abortController.abort()
    }
  }, [contractId, isSponsor])

  function getCurrencyValue(value?: number | null) {
    return value ? formatNumberToCurrency(value) : '-'
  }

  function getDateValue(value?: string | null) {
    return value ? FormatLocaleDate(value)! : '-'
  }

  async function handleViewReceipt() {
    if (!contractId) return

    await api
      .get<ReceiptResponse>(`/sponsorship/receipt/${contractId}`)
      .then(async ({ data }) => {
        if (!data?.url_recibo_patrocinador) {
          toast.error('URL do recibo não encontrada na resposta')
          return
        }

        await api
          .get(data.url_recibo_patrocinador, {
            responseType: 'blob',
          })
          .then(({ data: blob }) => {
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
          })
          .catch((error) => {
            toast.error(
              error.response?.data?.message || 'Erro ao abrir o recibo',
            )
          })
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || 'Erro ao buscar o recibo')
      })
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex justify-between">
        <h3 className="text-bios-800 text-xl font-black 2xl:text-2xl">
          Patrocínio
        </h3>

        {isSponsoring && (
          <div className="flex items-center gap-2">
            <Button
              onClick={(e) => {
                e.stopPropagation()
                handleViewReceipt()
              }}
              className="flex gap-2 p-0"
              variant="quaternary"
              data-testid="finances-tab-receipt-button"
            >
              <ReceiptText className="text-bios-500" />
              <h4 className="text-bios-title text-xl font-bold underline">
                Recibo
              </h4>
            </Button>

            <Link
              to={`/view-contract/${contractId}`}
              className="flex items-center gap-2"
              data-testid="finances-tab-contract-link"
            >
              <ContractEditIcon className="text-bios-500" />
              <h4 className="text-bios-title text-xl font-bold underline">
                Contrato
              </h4>
            </Link>
          </div>
        )}
      </div>

      <Select
        value={contractId}
        onValueChange={setContractId}
        disabled={sponsorshipList === null || isLoading}
        data-testid="finances-tab-sponsorship-select"
      >
        <div className="flex items-center gap-2">
          <SelectTrigger
            className="xl:w-1/2"
            data-testid="finances-tab-sponsorship-select-trigger"
          >
            <SelectValue
              data-testid="finances-tab-sponsorship-select-value"
              placeholder={
                sponsorshipList === null
                  ? 'Nenhum patrocínio encontrado'
                  : 'Selecione um patrocínio desejado'
              }
            />
          </SelectTrigger>
          {isLoading && <LoaderCircle className="text-bios-primary" />}
        </div>
        <SelectContent>
          {sponsorshipList?.map((sponsorship) => (
            <SelectItem
              key={sponsorship.id_contrato}
              value={sponsorship.id_contrato}
              data-testid="finances-tab-sponsorship-select-item"
            >
              {isSponsoring
                ? FormatLocaleDate(sponsorship.dt_cadastro)
                : `${sponsorship.nome_investidor} - ${FormatLocaleDate(sponsorship.dt_cadastro)}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-1 lg:gap-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          <h3 className="text-bios-800 text-xl font-black sm:mb-2 lg:col-span-4 2xl:text-2xl">
            Dados financeiros do contrato
          </h3>
          <CardData
            title="Valor bruto do patrocínio"
            value={getCurrencyValue(
              contractFinancesData?.Dados_financeiros_contrato.Valor_bruto,
            )}
          />
          <CardData
            title="Valor dos serviços 6BIOS"
            value={getCurrencyValue(
              contractFinancesData?.Dados_financeiros_contrato
                .Valor_servicos_6bios,
            )}
          />
          <CardData
            title="Valor dos serviços de agentes"
            value={getCurrencyValue(
              contractFinancesData?.Dados_financeiros_contrato
                .Valor_servicos_agentes,
            )}
          />
          <CardData
            title="Valor líquido do patrocínio"
            value={getCurrencyValue(
              contractFinancesData?.Dados_financeiros_contrato.Valor_liquido,
            )}
          />
          <CardData
            title="Quantidade de parcelas"
            value={
              contractFinancesData
                ? contractFinancesData?.Dados_financeiros_contrato.Parcelas.toString()
                : '-'
            }
          />
          <CardData
            title="Data de envio do contrato para assinatura"
            value={getDateValue(
              contractFinancesData?.Dados_financeiros_contrato.Dt_envio,
            )}
          />
          <CardData
            title="Data de assinatura do contrato"
            value={getDateValue(
              contractFinancesData?.Dados_financeiros_contrato.Dt_assinatura,
            )}
          />
          <CardData
            title="Data de início da vigência"
            value={getDateValue(
              contractFinancesData?.Dados_financeiros_contrato.Dt_vigencia,
            )}
          />
          {contractFinancesData?.Dados_financeiros_contrato.Dt_cancelamento && (
            <CardData
              title="Data de cancelamento do contrato"
              value={getDateValue(
                contractFinancesData.Dados_financeiros_contrato.Dt_cancelamento,
              )}
            />
          )}
        </div>

        <MonthlyStatement
          installments={contractFinancesData?.Dados_extrato.Parcelas}
          getCurrencyValue={getCurrencyValue}
          isSponsoring={isSponsoring}
          totalNetValue={
            contractFinancesData?.Dados_extrato.Total_valor_liquido
          }
        />
      </div>
    </div>
  )
}
